import React, { useCallback, useEffect, useMemo, useReducer, useContext } from 'react';
import { getApiClient } from '../api_client/get';
import {
  injectZendeskSnippetOnce,
  isZendeskApiReady
} from '../integrations/zendesk/internal';

const ZendeskContext = React.createContext(null);
const reducer = (state, action) => {
  switch (action.type) {
    case 'zendesk-ready': return { ...state, isReady: true };
    case 'open': return { ...state, shouldBeOpen: true };
    case 'close': return { ...state, shouldBeOpen: false };
    case 'toggle': return { ...state, shouldBeOpen: !state.shouldBeOpen };
    case 'set-locale': return { ...state, locale: action.locale };
    case 'set-jwt': return { ...state, zendeskJWT: action.jwt };
    default: throw new Error('unhandled action');
  }
};

const ZendeskProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, {
    isReady: false,
    shouldBeOpen: false,
    locale: null,
    zendeskJWT: null
  });
  const { isReady, shouldBeOpen, locale, zendeskJWT } = state;

  // Initialize zendesk once.
  useEffect(() => {
    let mounted = true;
    injectZendeskSnippetOnce()
        .then(() => {
        if (mounted && isZendeskApiReady()) {
            dispatch({ type: 'zendesk-ready' });
        }
        })
        .catch((e) => console.error('[Zendesk] init failed:', e));
    return () => { mounted = false; };
  }, []);

  // This side effect is meant to synchronize the Zendesk open state.
  useEffect(() => {
    if (!isReady) return;
    if (shouldBeOpen) {
        window.zE('messenger', 'open');
    } else {
        window.zE('messenger', 'close');
    }
  }, [isReady, shouldBeOpen]);

  // This side effect is meant to sync the Zendesk locale
  useEffect(() => {
    if (!isReady) return;
    window.zE('messenger:set', 'locale', locale);
  }, [isReady, locale]);

  // Sync the JWT with Zendesk.
  useEffect(() => {
    if (!isReady) return;
    if (zendeskJWT == null) {
      window.zE('messenger', 'logoutUser');
    } else {
      window.zE('messenger', 'loginUser', (done) => { done(zendeskJWT); });
    }
  }, [isReady, zendeskJWT]);

  const setLocale = useCallback((newLocale) => {
    dispatch({ type: 'set-locale', locale: newLocale });
  }, []);

  const openMessenger = useCallback(() => {
    dispatch({ type: 'open' });
  }, []);

  const closeMessenger = useCallback(() => {
    dispatch({ type: 'close' });
  }, []);

  const refreshIdentity = useCallback(async () => {
    try {
      const response = await getApiClient().getZendeskJWT();
      let jwt = null;
      if (response && response.data && response.data.jwt) {
        jwt = response.data.jwt;
      }
      dispatch({ type: 'set-jwt', jwt });
      return jwt;
    } catch (error) {
      console.error('[Zendesk] refreshIdentity failed:', error);
      dispatch({ type: 'set-jwt', jwt: null });
      return null;
    }
  }, []);

  const value = useMemo(() => ({
    isReady,
    openMessenger,
    closeMessenger,
    setLocale,
    refreshIdentity
  }), [setLocale, isReady, openMessenger, closeMessenger, refreshIdentity]);

  return (
    <ZendeskContext.Provider value={value}>
      {children}
    </ZendeskContext.Provider>
  );
};

export function useZendesk() {
  return useContext(ZendeskContext);
}

export default ZendeskProvider;
